import { getKey } from "@redotech/react-util/key";
import {
  CompensationMethodData,
  getGroupedReturnOptionsButtonText,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExchangeVariant,
  ReturnableItem,
  VariantExchangeItem,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { getNonPrimaryProducts } from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { ReturnOption } from "@redotech/redo-model/return-flow";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import BundlePartsIcon from "@redotech/redo-web/icon-old/bundle-parts.svg";
import BundleIcon from "@redotech/redo-web/icon-old/bundle.svg";
import EditPencil from "@redotech/redo-web/icon-old/edit-pencil.svg";
import * as classNames from "classnames";
import { memo, useState } from "react";
import { Product } from "./product";
import { ProductData } from "./product-data";
import * as productCss from "./product.module.css";
import * as returnOptions from "./return-options.module.css";
import { VariantExchange } from "./variant-exchange";
import { VariantExchangeFooter } from "./variant-exchange-footer";

enum BundleOptionType {
  BUNDLE = "bundle",
  PARTS = "parts",
}

interface BundleOption {
  type: BundleOptionType;
  icon: JSX.Element;
  title: string;
  description: string;
}

export const BundleType = memo(function BundleType({
  availableReturnOptions,
  onSubmit,
  returnableItem,
  settings,
}: {
  availableReturnOptions: ReturnOption[];
  onSubmit: (data: CompensationMethodData) => void;
  returnableItem: ReturnableItem;
  chooseVariant: (variant: ExchangeVariant) => void;
  settings: ReturnAppSettings;
}) {
  const [bundleOptionType, setBundleOptionType] =
    useState<BundleOptionType | null>(null);
  const bundleOptions: BundleOption[] = [
    {
      type: BundleOptionType.PARTS,
      icon: <BundlePartsIcon />,
      title: "Return individual items",
      description: "Exchanges only.",
    },
  ];

  if (availableReturnOptions.length > 1) {
    bundleOptions.unshift({
      type: BundleOptionType.BUNDLE,
      icon: <BundleIcon />,
      title: "Return all items in bundle",
      description: getGroupedReturnOptionsButtonText(availableReturnOptions),
    });
  }

  function handleClick(type: BundleOptionType) {
    setBundleOptionType(type);
  }

  if (bundleOptionType === BundleOptionType.PARTS) {
    return (
      <ExpandedProducts
        onSubmit={onSubmit}
        returnableItem={returnableItem}
        settings={settings}
      />
    );
  } else if (bundleOptionType === BundleOptionType.BUNDLE) {
    onSubmit({});
  }

  return (
    <div className={returnOptions.bundleOptionsWrapper}>
      <div className={returnOptions.bundleOptionsHeader}>
        How do you want to return your bundle?
      </div>
      <div className={returnOptions.bundleOptions}>
        {bundleOptions.map((option, index) => (
          <div
            className={returnOptions.bundleOption}
            key={index}
            onClick={() => handleClick(option.type)}
          >
            <div className={returnOptions.bundleOptionIcon}>{option.icon}</div>
            <div className={returnOptions.bundleOptionText}>
              <div className={returnOptions.bundleOptionTitle}>
                {option.title}
              </div>
              <div className={returnOptions.bundleOptionDescription}>
                {option.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

const ExpandedProducts = memo(function ExpandedProducts({
  returnableItem,
  settings,
  onSubmit,
}: {
  returnableItem: ReturnableItem;
  settings: ReturnAppSettings;
  onSubmit: (data: CompensationMethodData) => void;
}) {
  const [selectedProductIndex, setSelectedProductIndex] = useState<
    number | null
  >(null);
  const [visibleVariant, onVariantVisible] = useState<
    ExchangeVariant | undefined
  >(undefined);
  const nonPrimaryProducts = getNonPrimaryProducts(returnableItem);
  const [productVariantSelection, setProductVariantSelection] = useState<
    Record<number, ExchangeVariant | null>
  >(
    nonPrimaryProducts.reduce(
      (acc, _, index) => {
        acc[index] = null;
        return acc;
      },
      {} as Record<number, ExchangeVariant | null>,
    ),
  );

  const copyResource = getResource({ overrides: settings.resourceOverride });

  if (selectedProductIndex != null) {
    return (
      <>
        <ProductData
          expandedProductsLength={nonPrimaryProducts.length}
          isExpanded={false}
          product={nonPrimaryProducts[selectedProductIndex]}
          variant={visibleVariant}
        />
        <VariantExchange
          chooseVariant={(exchangeVariant: ExchangeVariant) => {
            setProductVariantSelection({
              ...productVariantSelection,
              [selectedProductIndex]: exchangeVariant,
            });
            setSelectedProductIndex(null);
          }}
          footer={VariantExchangeFooter}
          onBack={() => setSelectedProductIndex(null)}
          onVariantVisible={onVariantVisible}
          product={nonPrimaryProducts[selectedProductIndex]}
          settings={settings}
          submitItem={onSubmit}
        />
      </>
    );
  }

  return (
    <div className={productCss.cardContentExpanded}>
      {nonPrimaryProducts.map((product, index) => (
        <div className={productCss.productsContainer} key={getKey(product)}>
          <div className={classNames(productCss.productCard)}>
            <div>
              <Product returnProduct={product} />
            </div>
            {productVariantSelection[index] && (
              <div
                className={classNames(
                  productCss.tagContainer,
                  productCss.infoContainer,
                )}
              >
                <div className={productCss.tag}>
                  {copyResource.PENDING_ITEM_PILL_TEXT}
                </div>
              </div>
            )}
            <div className={productCss.selectButtonContainer}>
              <Button
                className={productCss.selectButton}
                onClick={() => {
                  setSelectedProductIndex(index);
                }}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.OUTLINED}
              >
                {productVariantSelection[index] ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                    }}
                  >
                    <EditPencil />
                    <div>Change</div>
                  </div>
                ) : (
                  "Select"
                )}
              </Button>
            </div>
          </div>
        </div>
      ))}
      {Object.values(productVariantSelection).filter((selection) => selection)
        .length > 0 && (
        <div className={productCss.expandedProductSubmitButton}>
          <Button
            className={productCss.submitButton}
            onClick={() => {
              onSubmit({
                expandedProductVariantExchanges: Object.values(
                  productVariantSelection,
                ).reduce((acc, exchangeVariant) => {
                  if (exchangeVariant) {
                    acc.push({
                      variantId: exchangeVariant.id,
                      productId: exchangeVariant.productId,
                      preserveProperties: true,
                      title: exchangeVariant.title,
                      image: exchangeVariant.image,
                      priceDifference: "ignore",
                    });
                  }
                  return acc;
                }, [] as VariantExchangeItem[]),
              });
            }}
            size={ButtonSize.LARGE}
            theme={ButtonTheme.PRIMARY}
          >
            {`Confirm ${Object.values(productVariantSelection).filter((selection) => selection).length} item exchange`}
          </Button>
        </div>
      )}
    </div>
  );
});
